import moment from 'moment';
import { ChangelogUpdateType, ChangelogValuesType } from 'src/components/History/types';
import { WebhookGlobalHistoryUpdateType } from 'src/types/webhookHistory';
import { getDateTimeFormat } from 'src/utils/timeConstants';

export const formatWebhookDateTime = (value: string | Date | null | undefined): string => {
    if (!value) {
        return '';
    }

    const parsed = moment(value);
    if (!parsed.isValid()) {
        return '';
    }

    return parsed.format(getDateTimeFormat());
};

const WEBHOOK_HISTORY_KEYS = [
    'WEBHOOK_TYPE',
    'METHOD',
    'ACTIVE',
    'URL',
    'PARAMETERS',
    'PAYLOAD_VERSION',
    'REGISTERED_AT',
    'UNREGISTERED_AT',
    'DEACTIVATION_EMAIL_SENT_AT',
    'SECRET_REGENERATED',
] as const;

const isDiffValue = (value: unknown): value is { old?: unknown; new?: unknown } =>
    typeof value === 'object' && value !== null && ('old' in value || 'new' in value);

const valuesEqual = (left: unknown, right: unknown): boolean => {
    if (left === right) {
        return true;
    }
    if (left == null && right == null) {
        return true;
    }
    return JSON.stringify(left) === JSON.stringify(right);
};

const applyUpdateToState = (state: Record<string, unknown>, data: Record<string, unknown>): Record<string, unknown> => {
    const next = { ...state };

    Object.entries(data).forEach(([key, value]) => {
        if (isDiffValue(value)) {
            next[key] = value.new;
            return;
        }
        next[key] = value;
    });

    return next;
};

const buildWebhookHistoryState = (updates: ChangelogUpdateType[], index: number): Record<string, unknown> => {
    let state: Record<string, unknown> = {};

    updates.slice(0, index).forEach((update) => {
        if (!update.data || typeof update.data !== 'object') {
            return;
        }
        state = applyUpdateToState(state, update.data as Record<string, unknown>);
    });

    return state;
};

const toHistoryDiff = (
    previous: Record<string, unknown>,
    current: Record<string, unknown>,
): Record<string, unknown> => {
    const diff: Record<string, unknown> = {};

    WEBHOOK_HISTORY_KEYS.forEach((key) => {
        if (!(key in current)) {
            return;
        }

        const nextValue = current[key];
        const previousValue = previous[key];

        if (!valuesEqual(previousValue, nextValue)) {
            diff[key] = { old: previousValue ?? null, new: nextValue };
        }
    });

    return diff;
};

const normalizeWebhookHistoryGroup = (updates: ChangelogUpdateType[]): ChangelogUpdateType[] => {
    if (!updates.length) {
        return [];
    }

    return updates.map((update, index) => {
        if (update.type === 'create' || !update.data || typeof update.data !== 'object') {
            return update;
        }

        const current = update.data as Record<string, unknown>;
        const alreadyDiff = Object.values(current).some(isDiffValue);

        if (alreadyDiff) {
            return update;
        }

        const previous = buildWebhookHistoryState(updates, index);
        const diff = toHistoryDiff(previous, current);

        return {
            ...update,
            data: diff as ChangelogValuesType,
        };
    });
};

export const normalizeWebhookHistoryUpdates = (updates: ChangelogUpdateType[] | undefined): ChangelogUpdateType[] => {
    if (!updates?.length) {
        return [];
    }

    const webhookIds = new Set(
        updates
            .map((update) => (update as { webhookId?: number }).webhookId)
            .filter((webhookId): webhookId is number => webhookId !== undefined),
    );

    if (webhookIds.size <= 1) {
        return normalizeWebhookHistoryGroup(updates);
    }

    const groupedUpdates = new Map<number, ChangelogUpdateType[]>();
    updates.forEach((update) => {
        const webhookId = (update as { webhookId?: number }).webhookId ?? 0;
        const group = groupedUpdates.get(webhookId) ?? [];
        group.push(update);
        groupedUpdates.set(webhookId, group);
    });

    const normalizedGroups = new Map<number, ChangelogUpdateType[]>();
    groupedUpdates.forEach((group, webhookId) => {
        normalizedGroups.set(webhookId, normalizeWebhookHistoryGroup(group));
    });

    const groupIndexes = new Map<number, number>();

    return updates.map((update) => {
        const webhookId = (update as { webhookId?: number }).webhookId ?? 0;
        const index = groupIndexes.get(webhookId) ?? 0;
        groupIndexes.set(webhookId, index + 1);
        return normalizedGroups.get(webhookId)?.[index] ?? update;
    });
};

const getWebhookHistoryContext = (
    updates: ChangelogUpdateType[],
    index: number,
    webhookId: number,
): WebhookGlobalHistoryUpdateType['webhookContext'] => {
    const stateBefore = buildWebhookHistoryState(updates, index);

    return {
        webhookId,
        WEBHOOK_TYPE: stateBefore.WEBHOOK_TYPE,
        METHOD: stateBefore.METHOD,
    };
};

export const enrichGlobalWebhookHistoryForDisplay = (
    updates: WebhookGlobalHistoryUpdateType[] | undefined,
): WebhookGlobalHistoryUpdateType[] => {
    const normalized = normalizeWebhookHistoryUpdates(updates) as WebhookGlobalHistoryUpdateType[];

    return normalized.map((update, index) => {
        const webhookId = update.webhookId;
        if (!webhookId) {
            return update;
        }

        if (update.type === 'create') {
            return {
                ...update,
                data: {
                    webhookId,
                    ...(update.data as ChangelogValuesType),
                } as ChangelogValuesType,
            };
        }

        if (!update.data || typeof update.data !== 'object') {
            return update;
        }

        return {
            ...update,
            webhookContext: getWebhookHistoryContext(normalized, index, webhookId),
            data: (update.data ?? {}) as ChangelogValuesType,
        };
    });
};
